import type { SelectOption } from '@diamond/shared/types';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useEffect, useRef, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { getVoucherProducts } from '../api';
import type { CreateVoucherRequest, VoucherDetailResponse } from '../types';
import { formatProductOption, mapVoucherDetailToForm } from '../utils/mappers';
import { useVoucher } from './api-hooks';

/**
 * Collect all unique product item codes from voucher configuration
 */
const collectProductCodes = (
  rewardConfig: VoucherDetailResponse['reward_configuration']
): Set<number> => {
  const codes = new Set<number>();

  rewardConfig?.reward_rule_products?.forEach((p) =>
    codes.add(p.product_item_code)
  );
  rewardConfig?.discount_products?.forEach((p) =>
    codes.add(p.product_item_code)
  );
  rewardConfig?.free_goods?.forEach((p) => codes.add(p.product_item_code));

  return codes;
};

/**
 * Fetch product details by item codes and return as Map
 */
const fetchProductDetails = async (
  itemCodes: Set<number>,
  token: string
): Promise<Map<number, SelectOption>> => {
  const productMap = new Map<number, SelectOption>();

  await Promise.all(
    Array.from(itemCodes).map(async (itemCode) => {
      try {
        const response = await getVoucherProducts(token, {
          page: 1,
          size: 1,
          search_query: String(itemCode),
        });

        const product = response.data.find((p) => p.item_code === itemCode);
        if (product) {
          productMap.set(itemCode, formatProductOption(product));
        }
      } catch (error) {
        console.error(`Failed to fetch product ${itemCode}:`, error);
      }
    })
  );

  return productMap;
};

/**
 * Map product details to form fields
 */
const mapProductsToFormFields = (
  voucherData: VoucherDetailResponse,
  productMap: Map<number, SelectOption>,
  form: UseFormReturn<CreateVoucherRequest>
) => {
  const rewardConfig = voucherData.reward_configuration;

  // Purchase products
  const purchaseProducts: SelectOption[] =
    rewardConfig?.reward_rule_products
      ?.map((p) => productMap.get(p.product_item_code))
      .filter((p): p is SelectOption => p !== undefined) ?? [];

  // Reward discount product (first item)
  const discountItemCode =
    rewardConfig?.discount_products?.[0]?.product_item_code;
  const rewardDiscountProduct = discountItemCode
    ? productMap.get(discountItemCode) ?? null
    : null;

  // Reward free product (first item)
  const freeGoodItemCode = rewardConfig?.free_goods?.[0]?.product_item_code;
  const rewardFreeProduct = freeGoodItemCode
    ? productMap.get(freeGoodItemCode) ?? null
    : null;

  // Update form values
  form.setValue('purchaseProducts', purchaseProducts);
  form.setValue('rewardDiscountProduct', rewardDiscountProduct);
  form.setValue('rewardFreeProduct', rewardFreeProduct);
};

export function useVoucherDetail() {
  const { id } = useParams<{ id: string }>();
  const token = useAuthStore((s) => s.access_token);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const loadedRef = useRef(false);

  const { data: voucherData, isLoading } = useVoucher(id || '');

  const form = useForm<CreateVoucherRequest>({
    mode: 'onChange',
    defaultValues: {
      banner_url: '',
      title: '',
      code: '',
      desc: '',
      period_start_date: '',
      period_end_date: '',
      target_configuration: {
        target_type: 'GENERAL',
        business_segmentations: [],
        distribution_channels: [],
        business_categories: [],
        shipto_csv_url: null,
        quota: 0,
        max_use_per_user: 1,
      },
      acquisition_configuration: {
        acquisition_type: 'REGULAR',
        valid_day_since_account_signup: 0,
      },
      condition_configuration: {
        condition_type: 'REGULAR',
        is_allow_combine_other_voucher: false,
        is_allow_combine_other_promo: false,
        is_allow_combine_other_special_price: false,
        min_purchase_amount: 0,
      },
      reward_configuration: {
        reward_type: 'DISCOUNT_PERCENT',
        is_bundling: false,
        min_total_quantity_all_item: null,
        reward_rule_products: [],
        free_goods: [],
        discount_products: [],
        discount_amount_value: null,
        discount_percentage_value: null,
        free_goods_quantity_value: null,
      },
    },
  });

  useEffect(() => {
    if (!voucherData || !token || loadedRef.current) return;

    const loadData = async () => {
      // Reset form with basic voucher data
      form.reset(mapVoucherDetailToForm(voucherData));

      // Collect and fetch product details
      const productCodes = collectProductCodes(
        voucherData.reward_configuration
      );
      if (productCodes.size === 0) {
        loadedRef.current = true;
        return;
      }

      setIsLoadingProducts(true);
      try {
        const productMap = await fetchProductDetails(productCodes, token);
        mapProductsToFormFields(voucherData, productMap, form);
        loadedRef.current = true;
      } catch (error) {
        console.error('Failed to load product details:', error);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    loadData();
  }, [voucherData, token, form]);

  return {
    form,
    voucherData,
    isLoading: isLoading || isLoadingProducts,
    voucherId: id,
  };
}
